import { FC, memo, useEffect, useState } from 'react';
import { IImageUploadedPlaceHolderProps } from 'shared/interfaces/IModalForm';
import { IUploadAttributes } from 'shared/interfaces/IUpload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ImageUploadedPlaceHolderContainer } from './ImageUploadedPlaceHolder.styles';
import { useDebounce } from 'use-debounce';
import UploadedImage from './UploadedImage/UploadedImage';

const getImageUrl = (attributes: IUploadAttributes) => {
  return attributes?.modified_attachment_url || attributes?.cropped_attachment_url || attributes?.attachment_url || '';
};

const ImageUploadedPlaceHolder: FC<IImageUploadedPlaceHolderProps> = ({
  handleRemoveImages,
  filesToBeSaved,
  handleOnDragEnd,
}) => {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [firstLoaded] = useDebounce(firstLoad, 1000);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    }
  }, []);

  const handleOnDragStart = (event: DragEndEvent) => {
    const { active } = event;
    if (!active) return;
    setActiveId(filesToBeSaved?.findIndex((data) => data.id === active.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleOnDragStart}
      onDragEnd={(e) => {
        setActiveId(null);
        handleOnDragEnd(e);
      }}
    >
      <SortableContext items={filesToBeSaved as any}>
        <ImageUploadedPlaceHolderContainer>
          {filesToBeSaved?.map((file, index) => {
            return (
              <UploadedImage
                firstLoad={firstLoaded}
                image_url={
                  file?.attributes && file?.attributes?.id
                    ? getImageUrl(file?.attributes)
                    : file.temp_modified_attachment || ''
                }
                key={index}
                id={file?.id || index}
                index={index}
                handleRemoveImages={handleRemoveImages}
              />
            );
          })}
        </ImageUploadedPlaceHolderContainer>
      </SortableContext>
      <DragOverlay>
        {activeId !== null ? (
          <UploadedImage
            firstLoad={firstLoaded}
            image_url={
              filesToBeSaved[activeId]?.attributes && filesToBeSaved[activeId]?.attributes?.id
                ? getImageUrl(filesToBeSaved[activeId]?.attributes)
                : filesToBeSaved[activeId].temp_modified_attachment || ''
            }
            id={filesToBeSaved[activeId]?.id || 0}
            index={0}
            handleRemoveImages={handleRemoveImages}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default memo(ImageUploadedPlaceHolder);
